import React from 'react';
import { 
  FaChartPie, FaChartBar, FaCalendarWeek, FaLightbulb 
} from 'react-icons/fa';

const AnalyticsSheet = ({ expenses, incomeStreams }) => {
  // Calculations
  const totalExpectedIncome = incomeStreams.reduce((acc, s) => acc + s.expectedAmount, 0);
  const totalActualIncome = incomeStreams.reduce((acc, s) => acc + s.actualReceived, 0);

  const totalExpenses = expenses.reduce((acc, b) => acc + b.amount, 0);
  const paidExpenses = expenses.filter(b => b.isPaid).reduce((acc, b) => acc + b.amount, 0);
  const unpaidExpenses = expenses.filter(b => !b.isPaid).reduce((acc, b) => acc + b.amount, 0);

  // Category breakdown
  const categoryData = {
    Business: expenses.filter(b => b.category === 'Business').reduce((sum, b) => sum + b.amount, 0),
    Household: expenses.filter(b => b.category === 'Household').reduce((sum, b) => sum + b.amount, 0),
    Others: expenses.filter(b => b.category === 'Others').reduce((sum, b) => sum + b.amount, 0),
  };

  // Priority breakdown
  const priorityData = {
    Important: expenses.filter(b => b.priority.startsWith('Important')).reduce((sum, b) => sum + b.amount, 0),
    Average: expenses.filter(b => b.priority.startsWith('Average')).reduce((sum, b) => sum + b.amount, 0),
    Less: expenses.filter(b => b.priority.startsWith('Less')).reduce((sum, b) => sum + b.amount, 0),
  };

  // 4 Weeks Monday vs Tuesday simulation
  // Monday inflows (1/4th of actual income per week)
  // Tuesday outflows (Important installments + due bills)
  const weeklyData = [1, 2, 3, 4].map(wk => {
    const mondayIn = Math.round(totalActualIncome / 4);
    // Rough estimate of week's obligations
    const tuesdayOut = Math.round((unpaidExpenses / 4) * (wk === 2 || wk === 3 ? 1.2 : 0.8));
    const net = mondayIn - tuesdayOut;
    return { week: `Week ${wk}`, mondayIn, tuesdayOut, net };
  });

  const maxWeekly = Math.max(...weeklyData.flatMap(w => [w.mondayIn, w.tuesdayOut]), 1);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>
          Financial Intelligence & Visual Analytics
        </h2>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.86rem' }}>
          Deep-dive analysis into cash liquidity, expense categorisation, and borrowing boundaries
        </p>
      </div>

      {/* Advisory Insight Card */}
      <div style={{
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '16px',
        padding: '18px 22px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        marginBottom: '28px'
      }}>
        <FaLightbulb style={{ color: '#2563eb', fontSize: '24px', flexShrink: 0, marginTop: '2px' }} />
        <div>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '0.98rem', fontWeight: 700, color: '#1e3a8a' }}>
            Decision Guidance & Strategic Recommendation
          </h4>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#1e40af', lineHeight: 1.5 }}>
            {unpaidExpenses > totalActualIncome ? (
              <>
                Your pending commitments (<strong>${unpaidExpenses.toLocaleString()}</strong>) currently exceed your realized monthly revenue (<strong>${totalActualIncome.toLocaleString()}</strong>). 
                To balance cashflow, prioritize <strong>Important (Contractual)</strong> invoices via the 3-Week Tuesday plan and leverage the extension request on <strong>Less Important</strong> bills.
              </>
            ) : (
              <>
                Healthy cash position! Your monthly revenue comfortably funds your current ledger commitments with remaining borrowing capacity. Continue keeping discretionary debt within your 30% ceiling.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Grid of Visual Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '24px', marginBottom: '28px' }}>
        {/* Chart 1: Cashflow Inflow vs Outflow Comparison */}
        <div className="bt-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaChartBar style={{ color: '#4f46e5' }} /> Cashflow Inflow vs Outflow
            </h3>
            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Monthly Run-Rate</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
            {/* Expected Income */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '6px' }}>
                <span style={{ color: '#475569' }}>Total Expected Revenue</span>
                <span style={{ fontWeight: 700 }}>${totalExpectedIncome.toLocaleString()}</span>
              </div>
              <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '7px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '100%', background: '#6366f1', borderRadius: '7px' }} />
              </div>
            </div>

            {/* Actual Received */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '6px' }}>
                <span style={{ color: '#475569' }}>Actual Realized Inflow</span>
                <span style={{ fontWeight: 700, color: '#059669' }}>${totalActualIncome.toLocaleString()}</span>
              </div>
              <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '7px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${totalExpectedIncome > 0 ? (totalActualIncome / totalExpectedIncome) * 100 : 0}%`,
                    background: '#10b981',
                    borderRadius: '7px'
                  }}
                />
              </div>
            </div>

            {/* Total Commitments */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '6px' }}>
                <span style={{ color: '#475569' }}>Total Ledger Commitments</span>
                <span style={{ fontWeight: 700, color: '#dc2626' }}>${totalExpenses.toLocaleString()}</span>
              </div>
              <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '7px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${totalExpectedIncome > 0 ? Math.min(100, (totalExpenses / totalExpectedIncome) * 100) : 100}%`,
                    background: '#ef4444',
                    borderRadius: '7px'
                  }}
                />
              </div>
            </div>

            {/* Paid vs Unpaid */}
            <div style={{ display: 'flex', gap: '14px', marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ flex: 1, padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: '0.74rem', color: '#166534', fontWeight: 700, textTransform: 'uppercase' }}>Paid & Cleared</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#15803d' }}>${paidExpenses.toLocaleString()}</div>
              </div>
              <div style={{ flex: 1, padding: '10px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                <div style={{ fontSize: '0.74rem', color: '#991b1b', fontWeight: 700, textTransform: 'uppercase' }}>Unpaid Pending</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#dc2626' }}>${unpaidExpenses.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Chart 2: Category Allocation */}
        <div className="bt-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaChartPie style={{ color: '#0ea5e9' }} /> Expense Category Allocation
            </h3>
            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>By Scope</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Business */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px' }}>
                <span style={{ fontWeight: 600, color: '#1e40af' }}>Business Operations</span>
                <span style={{ fontWeight: 700 }}>
                  ${categoryData.Business.toLocaleString()} ({totalExpenses > 0 ? Math.round((categoryData.Business / totalExpenses) * 100) : 0}%)
                </span>
              </div>
              <div style={{ height: '10px', background: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${totalExpenses > 0 ? (categoryData.Business / totalExpenses) * 100 : 0}%`,
                  background: '#2563eb',
                  borderRadius: '5px'
                }} />
              </div>
            </div>

            {/* Household */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px' }}>
                <span style={{ fontWeight: 600, color: '#b45309' }}>Household & Living</span>
                <span style={{ fontWeight: 700 }}>
                  ${categoryData.Household.toLocaleString()} ({totalExpenses > 0 ? Math.round((categoryData.Household / totalExpenses) * 100) : 0}%)
                </span>
              </div>
              <div style={{ height: '10px', background: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${totalExpenses > 0 ? (categoryData.Household / totalExpenses) * 100 : 0}%`,
                  background: '#d97706',
                  borderRadius: '5px'
                }} />
              </div>
            </div>

            {/* Others */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px' }}>
                <span style={{ fontWeight: 600, color: '#7e22ce' }}>Others & Discretionary</span>
                <span style={{ fontWeight: 700 }}>
                  ${categoryData.Others.toLocaleString()} ({totalExpenses > 0 ? Math.round((categoryData.Others / totalExpenses) * 100) : 0}%)
                </span>
              </div>
              <div style={{ height: '10px', background: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${totalExpenses > 0 ? (categoryData.Others / totalExpenses) * 100 : 0}%`,
                  background: '#a855f7',
                  borderRadius: '5px'
                }} />
              </div>
            </div>

            {/* Priority Segmentation */}
            <div style={{ marginTop: '10px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>
                Priority Breakdown (Debt Flexibility)
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ flex: priorityData.Important, background: '#fef2f2', border: '1px solid #fecaca', padding: '6px 8px', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#dc2626', fontWeight: 700 }}>Important</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 800 }}>${priorityData.Important.toLocaleString()}</div>
                </div>
                <div style={{ flex: priorityData.Average, background: '#fffbeb', border: '1px solid #fde68a', padding: '6px 8px', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#d97706', fontWeight: 700 }}>Average</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 800 }}>${priorityData.Average.toLocaleString()}</div>
                </div>
                <div style={{ flex: priorityData.Less, background: '#f1f5f9', border: '1px solid #e2e8f0', padding: '6px 8px', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#475569', fontWeight: 700 }}>Flexible / Less</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 800 }}>${priorityData.Less.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Monday Inflow vs Tuesday Outflow Liquidity Rhythm */}
      <div className="bt-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '0.98rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.02em', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCalendarWeek style={{ color: '#10b981' }} />
              Monday Receipts vs Tuesday Payments Weekly Rhythm
            </h3>
            <div className="bt-subtext">Ensuring Monday inflows arrive ahead of Tuesday installment tranches</div>
          </div>
          <div style={{ display: 'flex', gap: '14px', fontSize: '0.8rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', background: '#10b981', borderRadius: '2px', display: 'inline-block' }} />
              Monday Inflow
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', background: '#ef4444', borderRadius: '2px', display: 'inline-block' }} />
              Tuesday Outflow
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginTop: '16px' }}>
          {weeklyData.map((wk, idx) => (
            <div
              key={idx}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '12px',
                padding: '16px',
                backgroundColor: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>{wk.week}</span>
                <span style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '6px',
                  backgroundColor: wk.net >= 0 ? '#ecfdf5' : '#fef2f2',
                  color: wk.net >= 0 ? '#059669' : '#dc2626'
                }}>
                  Net: {wk.net >= 0 ? `+$${wk.net.toLocaleString()}` : `-$${Math.abs(wk.net).toLocaleString()}`}
                </span>
              </div>

              {/* Bar visualization */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#059669', marginBottom: '3px' }}>
                    <span>Monday In</span>
                    <strong>${wk.mondayIn.toLocaleString()}</strong>
                  </div>
                  <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(wk.mondayIn / maxWeekly) * 100}%`, background: '#10b981' }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#dc2626', marginBottom: '3px' }}>
                    <span>Tuesday Out</span>
                    <strong>${wk.tuesdayOut.toLocaleString()}</strong>
                  </div>
                  <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(wk.tuesdayOut / maxWeekly) * 100}%`, background: '#ef4444' }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSheet;
